"use client";
import { publicErrorMessage } from "@ocr-monorepo/shared";

import Uppy, { Body, UppyFile, UploadResult } from "@uppy/core";
import Tus from "@uppy/tus";
import { useParams } from "next/navigation";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useUppyEvent } from "@uppy/react";
import { FileViewerDialog } from "./file-viewer";
import { Button } from "@ocr-monorepo/ui/components/button";
import { SlidersHorizontal } from "lucide-react";
import UploadButton from "./upload-button";
import { useQueryClient } from "@tanstack/react-query";
import { useTRPC } from "@/lib/trpc";
import { useSession } from "@/lib/auth-client";
import { toast } from "sonner";
import { FileMeta } from "@/types/uppy";
import { Progress } from "@ocr-monorepo/ui/components/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@ocr-monorepo/ui/components/dialog";
import { Checkbox } from "@ocr-monorepo/ui/components/checkbox";
import { captureFirstFileUploaded } from "@/lib/analytics";

/**
 * Simple hash function for browser environments.
 * Falls back to simple string hashing if Web Crypto API is not available.
 */
function generateDeterministicFileId(
  userId: string,
  projectSlug: string,
  tableSlug: string | undefined,
  file: { name: string; size: number; type: string; lastModified: number },
): string {
  const fingerprintString = `${userId}-${projectSlug}-${tableSlug ?? "unassigned"}-${file.name}-${file.size}-${file.type}-${file.lastModified}`;

  let hash = 0;
  for (let i = 0; i < fingerprintString.length; i++) {
    const char = fingerprintString.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  return `f_${Math.abs(hash).toString(16)}`;
}

function getCanonicalUploadId(file: UppyFile<FileMeta, Body>): string | null {
  const uploadUrl = file.uploadURL ?? file.response?.uploadURL;
  if (!uploadUrl) return null;
  try {
    const pathname = new URL(uploadUrl, window.location.origin).pathname;
    const encodedId = pathname.split("/").filter(Boolean).at(-1);
    return encodedId ? decodeURIComponent(encodedId) : null;
  } catch {
    return null;
  }
}

async function generateFileFingerprint(
  userId: string,
  projectSlug: string,
  tableSlug: string | undefined,
  file: { name: string; size: number; type: string; lastModified: number },
): Promise<string> {
  const fingerprintString = `${userId}-${projectSlug}-${tableSlug ?? "unassigned"}-${file.name}-${file.size}-${file.type}-${file.lastModified}`;

  // Check if Web Crypto API is available (browser/Edge runtime)
  if (typeof crypto !== "undefined" && crypto.subtle) {
    const encoder = new TextEncoder();
    const data = encoder.encode(fingerprintString);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hash = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
    return hash;
  }

  // Fallback: simple hash (should never happen in browser, but safe fallback)
  let hash = 0;
  for (let i = 0; i < fingerprintString.length; i++) {
    const char = fingerprintString.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  const fallbackHash = Math.abs(hash).toString(16);
  return fallbackHash;
}

function createUppy(
  userId: string,
  projectSlug: string,
  getTableSlug: () => string | undefined,
  getUploadPurpose: () => "splitter_test" | undefined,
) {
  return new Uppy<FileMeta, Body>({
    restrictions: { allowedFileTypes: ["application/pdf", ".pdf"] },

    onBeforeFileAdded: (currentFile) => {
      const lastModified =
        currentFile.data instanceof File
          ? currentFile.data.lastModified
          : ((currentFile.meta?.lastModified as number | undefined) ?? 0);
      const fileId = generateDeterministicFileId(
        userId,
        projectSlug,
        getTableSlug(),
        {
          name: currentFile.name ?? "unknown",
          size: currentFile.size ?? 0,
          type: currentFile.type ?? "",
          lastModified,
        },
      );

      currentFile.id = fileId;
      return true;
    },
  }).use(Tus, {
    endpoint: "/api/upload",
    limit: 4,
    chunkSize: 5 * 1024 * 1024,

    fingerprint: (file, options) =>
      generateFileFingerprint(userId, projectSlug, getTableSlug(), { ...file }),
    allowedMetaFields: [
      "user_id",
      "projectSlug",
      "tableSlug",
      "filename",
      "name",
      "type",
      "file_size",
      "filetype",
      "file_id",
      "uploadPurpose",
      "routingMode",
    ],
  });
}

export type UploadManagerHandle = {
  openFileDialog: () => void;
  addFiles: (files: FileList | File[]) => void;
  reset: () => void;
};

type Props = {
  userId: string; //required to be able to properly hash the file id.
  projectSlug?: string;
  tableSlug?: string;
  uploadScope?: "project" | "staged-project" | "current-table";
  showButton?: boolean;
  embedded?: boolean;
  onQueueChange?: (queue: UploadQueueItem[]) => void;
  onUploadComplete?: (fileIds: string[]) => void | Promise<void>;
  remainingFileSlots?: number;
  prepareFiles?: (
    files: File[],
  ) => Promise<{ projectSlug: string; tableSlug: string } | void>;
  simulateFiles?: (files: File[]) => void | Promise<void>;
  uploadPurpose?: "splitter_test";
};

export type UploadQueueItemStatus =
  "queued" | "uploading" | "uploaded" | "error";

export type UploadQueueItem = {
  id: string;
  name: string;
  size: number;
  status: UploadQueueItemStatus;
  progress: number;
};

function toUploadQueueItem(
  file: UppyFile<FileMeta, Body>,
  isUploading: boolean,
): UploadQueueItem {
  const bytesUploaded = Number(file.progress?.bytesUploaded ?? 0);
  const bytesTotal = Number(file.progress?.bytesTotal ?? file.size ?? 0);
  const percentage =
    typeof file.progress?.percentage === "number"
      ? file.progress.percentage
      : 0;
  const progressFromBytes =
    bytesTotal > 0 ? Math.round((bytesUploaded / bytesTotal) * 100) : 0;
  const progress =
    file.progress?.uploadComplete === true
      ? 100
      : Math.max(progressFromBytes, percentage);

  let status: UploadQueueItemStatus = "queued";
  if (file.error) {
    status = "error";
  } else if (file.progress?.uploadComplete) {
    status = "uploaded";
  } else if (isUploading || bytesUploaded > 0) {
    status = "uploading";
  }

  return {
    id: file.id,
    name: file.name ?? "unknown",
    size: file.size ?? 0,
    status,
    progress,
  };
}
const UploadManager = forwardRef<UploadManagerHandle, Props>(
  (
    {
      userId,
      projectSlug,
      tableSlug,
      uploadScope = "project",
      showButton = true,
      embedded = false,
      onQueueChange,
      onUploadComplete,
      remainingFileSlots,
      prepareFiles,
      simulateFiles,
      uploadPurpose,
    }: Props,
    ref,
  ) => {
    const { data: session } = useSession();
    const params = useParams<{ tableSlug: string; projectSlug: string }>();
    const resolvedProjectSlug = projectSlug ?? params.projectSlug ?? "unknown";
    const resolvedTableSlug =
      uploadScope === "current-table" ? tableSlug ?? params.tableSlug : undefined;

    const uploadScopeRef = useRef({
      projectSlug: resolvedProjectSlug,
      tableSlug: resolvedTableSlug,
    });

    const [uppy, setUppy] = useState<Uppy<FileMeta, Body>>(() =>
      createUppy(
        userId,
        resolvedProjectSlug,
        () => uploadScopeRef.current.tableSlug,
        () => uploadPurpose,
      ),
    );

    const [filesToUpload, setFilesToUpload] = useState<
      UppyFile<FileMeta, Body>[]
    >([]);

    const [files, setFiles] = useState<UppyFile<FileMeta, Body>[]>([]);

    const [shouldUpdateFiles, setShouldUpdateFiles] = useState<Date>(
      () => new Date(),
    );

    const [uploadProgress, setUploadProgress] = useState({
      bytesUploaded: 0,
      bytesTotal: 0,
      percent: 0,
    });
    const [failedFiles, setFailedFiles] = useState<
      Array<{ id: string; name: string; error: string }>
    >([]);
    const [failedFilesModalOpen, setFailedFilesModalOpen] = useState(false);
    const [selectedFailedIds, setSelectedFailedIds] = useState<Set<string>>(
      () => new Set(),
    );
    const failedFilesMapRef = useRef(
      new Map<string, { name: string; error: string }>(),
    );
    const fileOrderRef = useRef<string[]>([]);
    const fileMetaMapRef = useRef(new Map<string, { name: string }>());
    const activeUploadIdsRef = useRef<Set<string>>(new Set());
    const pendingMetadataRef = useRef(new Set<Promise<void>>());
    const onQueueChangeRef = useRef(onQueueChange);
    const embeddedUploadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );

    const [isUploading, setIsUploading] = useState<boolean>(false);
    const trpc = useTRPC();

    const processFilesKey = trpc.documents.listDocuments.queryKey();
    const queryClient = useQueryClient();

    const resetProgress = () => {
      setUploadProgress({ bytesUploaded: 0, bytesTotal: 0, percent: 0 });
    };

    useEffect(() => {
      setUppy((previous) => {
        if (previous) {
          previous.cancelAll();
          previous.getFiles().forEach((file) => previous.removeFile(file.id));
        }
        uploadScopeRef.current = {
          projectSlug: resolvedProjectSlug,
          tableSlug: resolvedTableSlug,
        };
        return createUppy(
          userId,
          resolvedProjectSlug,
          () => uploadScopeRef.current.tableSlug,
          () => uploadPurpose,
        );
      });
      setFiles([]);
      setFilesToUpload([]);
      fileOrderRef.current = [];
      fileMetaMapRef.current.clear();
      failedFilesMapRef.current.clear();
      pendingMetadataRef.current.clear();
      if (embeddedUploadTimerRef.current) {
        clearTimeout(embeddedUploadTimerRef.current);
        embeddedUploadTimerRef.current = null;
      }
      setFailedFiles([]);
      setSelectedFailedIds(new Set());
      setIsUploading(false);
      resetProgress();
    }, [userId, resolvedProjectSlug, resolvedTableSlug, uploadPurpose, uploadScope]);

    const updateProgressFromUppy = () => {
      if (!uppy) return;
      const activeIds = activeUploadIdsRef.current;
      const currentFiles = uppy
        .getFiles()
        .filter((file) => activeIds.has(file.id));

      let bytesUploaded = 0;
      let bytesTotal = 0;

      currentFiles.forEach((file) => {
        const progress = file.progress;
        const total =
          typeof progress?.bytesTotal === "number"
            ? progress.bytesTotal
            : (file.size ?? 0);
        const uploaded =
          typeof progress?.bytesUploaded === "number"
            ? progress.bytesUploaded
            : 0;
        bytesTotal += total;
        bytesUploaded += uploaded;
      });

      const percent = bytesTotal
        ? Math.min(100, Math.round((bytesUploaded / bytesTotal) * 100))
        : 0;

      setUploadProgress({ bytesUploaded, bytesTotal, percent });
    };

    const buildFailedList = (failed: UppyFile<FileMeta, Body>[]) => {
      const failedIds = new Set(failed.map((file) => file.id));
      const orderedIds = fileOrderRef.current.filter((id) => failedIds.has(id));
      const missingIds = failed
        .map((file) => file.id)
        .filter((id) => !orderedIds.includes(id));
      const allIds = [...orderedIds, ...missingIds];

      return allIds.map((id) => {
        const failure = failedFilesMapRef.current.get(id);
        const name =
          failure?.name ?? fileMetaMapRef.current.get(id)?.name ?? "unknown";
        const error = failure?.error ?? "Upload failed";
        return { id, name, error };
      });
    };

    const handleRetryFailed = () => {
      const idsToRetry = selectedFailedIds.size
        ? Array.from(selectedFailedIds)
        : failedFiles.map((file) => file.id);

      idsToRetry.forEach((id) => {
        uppy.retryUpload(id);
      });

      setFailedFilesModalOpen(false);
    };

    useEffect(() => {
      if (!uppy) return;
      const uppyFiles = [...uppy.getFiles()];
      setFiles(uppyFiles);
    }, [shouldUpdateFiles, uppy]);

    useEffect(() => {
      onQueueChangeRef.current = onQueueChange;
    }, [onQueueChange]);

    useEffect(() => {
      onQueueChangeRef.current?.(
        files.map((file) => toUploadQueueItem(file, isUploading)),
      );
    }, [files, isUploading]);

    useEffect(() => {
      if (!uppy) return;

      const handleUploadStart = () => {
        const currentFiles = uppy.getFiles();
        setFilesToUpload(currentFiles);
        setFiles(currentFiles);
        activeUploadIdsRef.current = new Set(
          currentFiles.map((file) => file.id),
        );
        setIsUploading(true);
        setFailedFiles([]);
        setSelectedFailedIds(new Set());
        failedFilesMapRef.current.clear();
        resetProgress();
      };

      const handleUploadProgress = () => {
        updateProgressFromUppy();
        setFiles([...uppy.getFiles()]);
      };

      const handleUploadError = (
        file: UppyFile<FileMeta, Body> | undefined,
        error: { message?: string },
      ) => {
        const errorMessage = error?.message?.includes(
          "upload has already been created",
        )
          ? "File already uploaded"
          : error?.message || "Upload failed";

        console.error("[UploadManager] Upload failed", {
          fileName: file?.name ?? "unknown",
          fileId: file?.id ?? null,
          projectSlug: uploadScopeRef.current.projectSlug,
          tableSlug: uploadScopeRef.current.tableSlug ?? null,
          message: errorMessage,
        });

        if (file) {
          failedFilesMapRef.current.set(file.id, {
            name: file.name || "unknown",
            error: errorMessage,
          });
        }
      };

      const handleComplete = (result: UploadResult<FileMeta, Body>) => {
        queryClient.invalidateQueries({ queryKey: processFilesKey });

        setIsUploading(false);
        updateProgressFromUppy();

        const successful = result.successful ?? [];
        const failed = result.failed ?? [];
        const successCount = successful.length;
        const failureCount = failed.length;
        const failedList = buildFailedList(failed);

        setFailedFiles(failedList);
        setSelectedFailedIds(new Set());

        const uploadedFileIds = successful
          .map(getCanonicalUploadId)
          .filter((fileId): fileId is string => typeof fileId === "string");
        if (uploadedFileIds.length > 0) {
          captureFirstFileUploaded(userId, {
            project_slug: uploadScopeRef.current.projectSlug,
            table_slug: uploadScopeRef.current.tableSlug,
            file_count: uploadedFileIds.length,
          });
          void onUploadComplete?.(uploadedFileIds);
        }

        if (failureCount === 0 && successCount > 0) {
          toast.success("Upload complete", {
            description: `${successCount} file${successCount !== 1 ? "s" : ""} uploaded successfully`,
          });
        } else if (failureCount > 0) {
          toast.warning("Upload complete", {
            description: `${successCount} succeeded, ${failureCount} failed`,
            action: {
              label: "View failed files",
              onClick: () => setFailedFilesModalOpen(true),
            },
          });
        }

        if (failureCount === 0) {
          if (!embedded) {
            uppy.cancelAll();
            uppy.getFiles().forEach((file) => uppy.removeFile(file.id));
            setFiles([]);
            setFilesToUpload([]);
            fileOrderRef.current = [];
            fileMetaMapRef.current.clear();
          } else {
            setFiles([...uppy.getFiles()]);
            setFilesToUpload([]);
          }
          activeUploadIdsRef.current.clear();
          resetProgress();
          return;
        }

        if (!embedded) {
          successful.forEach((file) => {
            uppy.removeFile(file.id);
          });
        }

        const failedIds = new Set(failed.map((file) => file.id));
        if (!embedded) {
          fileOrderRef.current = fileOrderRef.current.filter((id) =>
            failedIds.has(id),
          );
        }
        activeUploadIdsRef.current = new Set(fileOrderRef.current);
        setFiles(uppy.getFiles());
      };

      uppy.on("upload", handleUploadStart);
      uppy.on("upload-progress", handleUploadProgress);
      uppy.on("progress", handleUploadProgress);
      uppy.on("upload-error", handleUploadError);
      uppy.on("complete", handleComplete);

      return () => {
        uppy.off("upload", handleUploadStart);
        uppy.off("upload-progress", handleUploadProgress);
        uppy.off("progress", handleUploadProgress);
        uppy.off("upload-error", handleUploadError);
        uppy.off("complete", handleComplete);
      };
    }, [uppy, queryClient, processFilesKey, onUploadComplete]);

    const inputRef = useRef<HTMLInputElement>(null);
    useEffect(() => {}, [inputRef]);
    const [open, onOpenChange] = useState<boolean>(false);

    const scheduleEmbeddedUpload = () => {
      if (!embedded || !uppy) return;

      if (embeddedUploadTimerRef.current) {
        clearTimeout(embeddedUploadTimerRef.current);
      }

      embeddedUploadTimerRef.current = setTimeout(() => {
        embeddedUploadTimerRef.current = null;

        void (async () => {
          const pendingMetadata = Array.from(pendingMetadataRef.current);
          if (pendingMetadata.length > 0) {
            await Promise.allSettled(pendingMetadata);
          }

          const readyFiles = uppy.getFiles().filter((file) => {
            return !file.error && file.progress?.uploadComplete !== true;
          });

          if (readyFiles.length === 0) return;
          await uppy.upload();
        })();
      }, 50);
    };

    useUppyEvent(uppy, "file-added", (uppyFile) => {
      if (!session?.user.id || !uppy) return;
      const file = uppy.getFile(uppyFile.id);

      if (!fileOrderRef.current.includes(file.id)) {
        fileOrderRef.current.push(file.id);
      }

      fileMetaMapRef.current.set(file.id, { name: file.name || "unknown" });

      const metadataPromise = (async () => {
        const fileId = await generateFileFingerprint(
          session.user.id,
          uploadScopeRef.current.projectSlug,
          uploadScopeRef.current.tableSlug,
          {
            lastModified:
              file.data instanceof File ? file.data.lastModified : 0,
            size: file.size ?? file.data?.size ?? 0,
            name: file.name ?? "unknown",
            type: file.type ?? "",
          },
        );

        uppy.setFileMeta(file.id, {
          projectSlug: uploadScopeRef.current.projectSlug,
          ...(uploadScopeRef.current.tableSlug
            ? { tableSlug: uploadScopeRef.current.tableSlug }
            : {}),
          user_id: session.user.id,
          file_id: fileId,
          filename: file.name,
          name: file.name,
          file_size: file.size,
          type: file.type,
          lastModified: file.data instanceof File ? file.data.lastModified : 0,
          filetype: file.type,
          ...(uploadPurpose ? { uploadPurpose } : {}),
          routingMode:
            uploadScope === "current-table"
              ? "direct"
              : uploadScope === "staged-project"
                ? "deferred"
                : "automatic",
        });
      })();

      pendingMetadataRef.current.add(metadataPromise);
      void metadataPromise
        .finally(() => {
          pendingMetadataRef.current.delete(metadataPromise);
        })
        .catch(() => {
          /* handled below */
        });

      void metadataPromise.then(
        () => {
          setShouldUpdateFiles(new Date());
          scheduleEmbeddedUpload();
        },
        (error) => {
          console.error(
            "[UploadManager] Failed to prepare file metadata",
            error,
          );
          failedFilesMapRef.current.set(file.id, {
            name: file.name || "unknown",
            error: "Failed to prepare upload metadata",
          });
          setShouldUpdateFiles(new Date());
          scheduleEmbeddedUpload();
        },
      );

      if (!embedded) {
        onOpenChange(true);
      }
    });

    const addFilesToQueue = async (incoming: FileList | File[]) => {
      const acceptedPdfFiles = Array.from(incoming ?? []).filter((file) => {
        return (
          file.type === "application/pdf" ||
          file.name.toLowerCase().endsWith(".pdf")
        );
      });
      const availableSlots =
        typeof remainingFileSlots === "number"
          ? Math.max(
              0,
              remainingFileSlots -
                uppy
                  .getFiles()
                  .filter((file) => file.progress?.uploadComplete !== true)
                  .length,
            )
          : acceptedPdfFiles.length;
      const filesArray = acceptedPdfFiles.slice(0, availableSlots);
      const skippedCount = acceptedPdfFiles.length - filesArray.length;

      if (skippedCount > 0) {
        toast.warning(
          availableSlots === 0
            ? "Temporary conversion file limit reached"
            : `${skippedCount} PDF${skippedCount === 1 ? " was" : "s were"} not added`,
          {
            description:
              availableSlots === 0
                ? "Upgrade your account to upload more PDFs."
                : `This temporary conversion has ${availableSlots} file slot${availableSlots === 1 ? "" : "s"} remaining.`,
          },
        );
      }
      if (filesArray.length === 0) return;
      if (simulateFiles) {
        await simulateFiles(filesArray);
        return;
      }
      if (prepareFiles) {
        const scope = await prepareFiles(filesArray);
        if (scope) uploadScopeRef.current = scope;
      }
      filesArray.forEach((file) => {
        try {
          uppy?.addFile(file as any);
        } catch (error) {
          const message = error instanceof Error ? error.message : "";
          if (message.toLowerCase().includes("exists")) {
            toast.warning(`${file.name} is already in the queue`);
          } else {
            toast.error(publicErrorMessage("file_processing_error"));
          }
        }
      });
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files) {
        void addFilesToQueue(event.target.files).catch((error) => {
          toast.error("Could not prepare PDF", {
            description: publicErrorMessage("file_processing_error"),
          });
        });
      }
      event.target.value = "";
    };

    useImperativeHandle(
      ref,
      () => ({
        openFileDialog: () => {
          inputRef.current?.click();
        },
        addFiles: (incomingFiles) => {
          void addFilesToQueue(incomingFiles);
        },
        reset: () => {
          uppy.cancelAll();
          uppy.getFiles().forEach((file) => uppy.removeFile(file.id));
          setFiles([]);
          setFilesToUpload([]);
          setFailedFiles([]);
          setSelectedFailedIds(new Set());
          setIsUploading(false);
          fileOrderRef.current = [];
          fileMetaMapRef.current.clear();
          failedFilesMapRef.current.clear();
          activeUploadIdsRef.current.clear();
          resetProgress();
        },
      }),
      [remainingFileSlots, simulateFiles, uppy],
    );

    // Don't render until uppy is initialized
    if (!uppy || (!session && !simulateFiles)) {
      return null;
    }

    return (
      <>
        <UploadButton
          handleInputChange={handleInputChange}
          inputRef={inputRef}
          showButton={showButton && files.length === 0}
        />
        {showButton && (
          <>
            {!isUploading && files.length > 0 && (
              <Button
                variant={"outline"}
                size="sm"
                onClick={() => {
                  if (files.length > 0) {
                    onOpenChange(true);
                  }
                }}
              >
                {files.length > 0 && (
                  <>
                    <span>{files.length} File(s) selected</span>
                    <SlidersHorizontal />
                  </>
                )}
              </Button>
            )}
            {isUploading && (
              <div className="w-full max-w-xs space-y-1 rounded-md border h-9 border-border/60 bg-background/70 p-3">
                <Progress value={uploadProgress.percent} className="h-2" />
                <p
                  className=" text-muted-foreground"
                  style={{ fontSize: "8px" }}
                >
                  {uploadProgress.percent}% uploaded
                </p>
              </div>
            )}
          </>
        )}

        {!embedded && (
          <FileViewerDialog
            doUpload={() => {}}
            onCancelAll={() => {
              onOpenChange(false);
            }}
            open={open}
            uppy={uppy}
            onOpenChange={onOpenChange}
            files={files}
          />
        )}

        <Dialog
          open={failedFilesModalOpen}
          onOpenChange={setFailedFilesModalOpen}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Failed uploads</DialogTitle>
              <DialogDescription>
                Review failed uploads and retry the ones you want to send again.
              </DialogDescription>
            </DialogHeader>

            <div className="max-h-64 space-y-3 overflow-y-auto">
              {failedFiles.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No failed files.
                </p>
              ) : (
                failedFiles.map((file) => (
                  <div key={file.id} className="flex items-start gap-3">
                    <Checkbox
                      checked={selectedFailedIds.has(file.id)}
                      onCheckedChange={(checked) => {
                        setSelectedFailedIds((prev) => {
                          const next = new Set(prev);
                          if (checked) {
                            next.add(file.id);
                          } else {
                            next.delete(file.id);
                          }
                          return next;
                        });
                      }}
                      aria-label={`Select ${file.name}`}
                    />
                    <div className="space-y-1">
                      <p className="text-sm font-medium">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {file.error}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setFailedFilesModalOpen(false)}
              >
                Close
              </Button>
              <Button
                onClick={handleRetryFailed}
                disabled={failedFiles.length === 0}
              >
                Retry selected
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  },
);

UploadManager.displayName = "UploadManager";

export default UploadManager;
